import browser from "@raiden/library-ui/constants/browser";
import { CAMPAIGNS_TYPE_VALUE_DESTINATION } from "@raiden/library-ui/constants/campaigns";
import { useApi } from "@raiden/library-ui/hooks/useApi";
import { serverApiFetcher } from "@raiden/library-ui/hooks/useApiFetcher";
import generateApiUrl from "@raiden/library-ui/libraries/utils/generateApiUrl";
import generateFrontUrl from "@raiden/library-ui/libraries/utils/generateFrontUrl";
import { useIntl } from "react-intl";
import Seo from "../../components/Seo";
import {
  CampaignsSearch,
  DEFAULT_VALUES,
  generateCampaignsSearchUrl,
} from "../../containers/Campaigns/Search";
import { frontBaseUri } from "@raiden/library-ui/constants/routers/front";
import { getString } from "@raiden/library-ui/hooks/router/useRouterString";
import { apiGetErrorStatus } from "@raiden/library-ui/helpers/api";
import { getNumber } from "@raiden/library-ui/hooks/router/useRouterNumber";

function getUrlConfiguration() {
  return generateApiUrl({
    id: "@configuration",
    query: {
      fields: ["campaigns_categories"],
    },
  });
}

/**
 * Resolves the status code of the page based on the response and error.
 * @param {object} params
 * @param {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Campaign").Campaign[]>} [params.response]
 * @param {import("@raiden/library-ui/types/Api/ApiError").ApiError} [params.error]
 * @returns {number | undefined}
 */
function getStatus({ response, error }) {
  if (response) {
    return 200;
  }
  if (error) {
    return apiGetErrorStatus({ error: error });
  }
  return undefined;
}

/**
 * @typedef {object} Props
 * @property {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Configuration").Configuration>} [configurationResponse]
 * @property {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Campaign").Campaign[]>} [campaignSearchResponse]
 * @property {number} [serverStatus]
 * @property {import("../../containers/Campaigns/Search").CampaignSearchFormValues} initialFields
 */
/**
 * @param {Props} props
 */
function Page({
  configurationResponse,
  campaignSearchResponse,
  initialFields,
}) {
  const intl = useIntl();

  /** @type {import("@raiden/library-ui/hooks/useApi").UseApi<import("@raiden/library-ui/types/Configuration").Configuration>} */
  const { swrResponse: swrResponseConfiguration } = useApi(
    configurationResponse === undefined ? getUrlConfiguration() : null,
    {
      swrConfig: {
        fallbackData: configurationResponse,
      },
    },
  );

  const page = initialFields.page;

  return (
    <>
      <Seo
        title={intl.formatMessage({ defaultMessage: "Destinations" })}
        description={intl.formatMessage({
          defaultMessage: "Découvrez nos destinations incontournables.",
        })}
        links={(locale) =>
          generateFrontUrl({
            id: "@front.destinations.search",
            parameters: {
              locale,
            },
          })
        }
        canonicalLink={(locale) =>
          generateFrontUrl({
            id: "@front.destinations.search",
            parameters: {
              locale,
            },
            ...(page &&
              page !== 1 && {
                query: {
                  page,
                },
              }),
          })
        }
        alternateLinks={(locale) =>
          generateFrontUrl({
            id: "@front.destinations.search",
            parameters: {
              locale,
            },
            ...(page &&
              page !== 1 && {
                query: {
                  page,
                },
              }),
          })
        }
      />

      <CampaignsSearch
        campaignType={CAMPAIGNS_TYPE_VALUE_DESTINATION}
        swrResponseConfiguration={swrResponseConfiguration}
        campaignSearchResponse={campaignSearchResponse}
        initialFields={initialFields}
        campaignUrl={generateFrontUrl({
          id: "@front.destinations.search",
        })}
      />
    </>
  );
}

export default Page;

/**
 * @typedef {import("../../types/Page").PageInitialProps & Props} InitialProps
 */
/**
 * @param {import("../../types/AppContext").PageContext} context
 * @returns {Promise<InitialProps>}
 */
Page.getInitialProps = async function (context) {
  const term = getString(context.query.term) ?? DEFAULT_VALUES.term;
  const sort = getString(context.query.sort) ?? DEFAULT_VALUES.sort;
  const categoryId =
    getString(context.query.categoryId) ?? DEFAULT_VALUES.categoryId;
  const page = getNumber(context.query.page) ?? 1;

  /** @type {InitialProps} */
  const values = {
    layout: "public",
    initialFields: {
      ...DEFAULT_VALUES,
      term,
      sort,
      categoryId,
      page,
    },
  };

  if (!browser) {
    let postError = undefined;

    /** @type {import("@raiden/library-ui/types/Api/ApiResponse").ApiResponse<import("@raiden/library-ui/types/Campaign").Campaign[]> | undefined} */
    const campaignSearchResponse = await serverApiFetcher(
      generateCampaignsSearchUrl({
        fields: values.initialFields,
        key: page - 1,
        campaignType: CAMPAIGNS_TYPE_VALUE_DESTINATION,
      }),
      undefined,
      {
        origin: frontBaseUri.toString(),
        cookies: context.cookies,
      },
    ).catch(
      (
        /** @type {import("@raiden/library-ui/types/Api/ApiError").ApiError} */ error,
      ) => {
        postError = error;
        return undefined;
      },
    );

    values.campaignSearchResponse = campaignSearchResponse;

    const serverStatus = getStatus({
      response: campaignSearchResponse,
      error: postError,
    });

    values.serverStatus = serverStatus;

    if (context.res && serverStatus !== undefined) {
      context.res.statusCode = serverStatus;
    }

    values.configurationResponse = await serverApiFetcher(
      getUrlConfiguration(),
    ).catch(() => undefined);
  }

  return values;
};
